/**
 * Grid DOM Constants
 *
 * Centralized constants for CSS classes, data attributes, and selectors
 * used throughout the grid. Use these instead of magic strings.
 *
 * Note: Some constants here are used by plugins but defined in core because:
 * 1. The core CSS needs to style these elements (e.g., sticky positioning)
 * 2. Multiple plugins may share the same class names
 *
 * Plugins can define their own additional constants and export them.
 * See each plugin's index.ts for plugin-specific exports.
 */

// #region CSS Classes

/**
 * CSS class names used in the grid's DOM.
 * Use these when adding/removing classes or querying elements.
 *
 * Classes are organized by:
 * - Core: Used by the grid core for structure and basic functionality
 * - Shared: Used by multiple features/plugins, styled by core CSS
 *
 * @category Plugin Development
 * @since 0.1.1
 */
export const GridClasses = {
  // ─── Core Structure ───────────────────────────────────────────────
  ROOT: 'tbw-grid-root',
  HEADER: 'header',
  HEADER_ROW: 'header-row',
  HEADER_CELL: 'header-cell',

  // Body structure
  ROWS_VIEWPORT: 'rows-viewport',
  ROWS_SPACER: 'rows-spacer',
  ROWS_CONTAINER: 'rows',

  // Row elements
  DATA_ROW: 'data-grid-row',
  GROUP_ROW: 'group-row',

  // Cell elements
  DATA_CELL: 'cell',

  // ─── Core States ──────────────────────────────────────────────────
  SELECTED: 'selected',
  FOCUSED: 'focused',
  EDITING: 'editing',
  EXPANDED: 'expanded',
  COLLAPSED: 'collapsed',
  DRAGGING: 'dragging',
  RESIZING: 'resizing',

  // Sorting (core feature)
  SORTABLE: 'sortable',
  SORTED_ASC: 'sorted-asc',
  SORTED_DESC: 'sorted-desc',

  // Visibility
  HIDDEN: 'hidden',

  // ─── Shared Classes (used by plugins, styled by core CSS) ────────
  // These are defined here because core CSS provides the base styling.
  // Plugins apply these classes; core CSS defines how they look.

  // Sticky positioning (PinnedColumnsPlugin applies, core CSS styles)
  STICKY_LEFT: 'sticky-left',
  STICKY_RIGHT: 'sticky-right',

  // Pinned rows (PinnedRowsPlugin applies, core CSS styles)
  PINNED_TOP: 'pinned-top',
  PINNED_BOTTOM: 'pinned-bottom',

  // Tree structure (TreePlugin applies, core CSS styles)
  TREE_TOGGLE: 'tree-toggle',
  TREE_INDENT: 'tree-indent',

  // Grouping (GroupingRowsPlugin applies, core CSS styles)
  GROUP_TOGGLE: 'group-toggle',
  GROUP_LABEL: 'group-label',
  GROUP_COUNT: 'group-count',

  // Selection (SelectionPlugin applies, core CSS styles)
  RANGE_SELECTION: 'range-selection',
  SELECTION_OVERLAY: 'selection-overlay',
} as const;

// #endregion

// #region Data Attributes

/**
 * Data attribute names used on grid elements.
 * Use these when getting/setting data attributes.
 *
 * @category Plugin Development
 * @since 0.1.1
 */
export const GridDataAttrs = {
  // ─── Core Attributes ──────────────────────────────────────────────
  FIELD: 'data-field',

  // ─── Shared Attributes (used by plugins) ──────────────────────────
  GROUP_KEY: 'data-group-key', // GroupingRowsPlugin
} as const;

// #endregion

// #region Selectors

/**
 * Common CSS selectors for querying grid elements.
 * Built from the class constants for consistency.
 *
 * @category Plugin Development
 * @since 0.1.1
 */
export const GridSelectors = {
  ROOT: `.${GridClasses.ROOT}`,
  HEADER: `.${GridClasses.HEADER}`,
  HEADER_ROW: `.${GridClasses.HEADER_ROW}`,
  HEADER_CELL: `.${GridClasses.HEADER_CELL}`,
  ROWS_VIEWPORT: `.${GridClasses.ROWS_VIEWPORT}`,
  ROWS_CONTAINER: `.${GridClasses.ROWS_CONTAINER}`,
  DATA_ROW: `.${GridClasses.DATA_ROW}`,
  DATA_CELL: `.${GridClasses.DATA_CELL}`,
  GROUP_ROW: `.${GridClasses.GROUP_ROW}`,

  // By data attribute
  CELL_BY_FIELD: (field: string) => `.${GridClasses.DATA_CELL}[${GridDataAttrs.FIELD}="${field}"]`,

  // State selectors
  SELECTED_ROWS: `.${GridClasses.DATA_ROW}.${GridClasses.SELECTED}`,
  EDITING_CELL: `.${GridClasses.DATA_CELL}.${GridClasses.EDITING}`,
} as const;

// #endregion

// #region CSS Custom Properties

/**
 * CSS custom property names for theming.
 * Use these when programmatically setting styles.
 *
 * @category Plugin Development
 * @since 0.1.1
 */
export const GridCSSVars = {
  // Colors
  COLOR_BG: '--tbw-color-bg',
  COLOR_FG: '--tbw-color-fg',
  COLOR_FG_MUTED: '--tbw-color-fg-muted',
  COLOR_BORDER: '--tbw-color-border',
  COLOR_ACCENT: '--tbw-color-accent',
  COLOR_HEADER_BG: '--tbw-color-header-bg',
  COLOR_HEADER_FG: '--tbw-color-header-fg',
  COLOR_SELECTION: '--tbw-color-selection',
  COLOR_ROW_HOVER: '--tbw-color-row-hover',
  COLOR_ROW_ALT: '--tbw-color-row-alt',

  // Sizing
  ROW_HEIGHT: '--tbw-row-height',
  HEADER_HEIGHT: '--tbw-header-height',
  CELL_PADDING: '--tbw-cell-padding',

  // Typography
  FONT_FAMILY: '--tbw-font-family',
  FONT_SIZE: '--tbw-font-size',

  // Borders
  BORDER_RADIUS: '--tbw-border-radius',
  FOCUS_OUTLINE: '--tbw-focus-outline',
} as const;

// #endregion

// Type helpers

/**
 * Union of all CSS class names used in the grid's DOM.
 * Use `GridClasses` to access individual values by key.
 *
 * @category Plugin Development
 * @since 0.1.1
 */
export type GridClassName = (typeof GridClasses)[keyof typeof GridClasses];

/**
 * Union of all `data-*` attribute names used on grid elements.
 * Use `GridDataAttrs` to access individual values by key.
 *
 * @category Plugin Development
 * @since 0.1.1
 */
export type GridDataAttr = (typeof GridDataAttrs)[keyof typeof GridDataAttrs];

/**
 * Union of all CSS custom property names (`--tbw-*`) available for theming.
 * Use `GridCSSVars` to access individual values by key.
 *
 * @category Plugin Development
 * @since 0.1.1
 */
export type GridCSSVar = (typeof GridCSSVars)[keyof typeof GridCSSVars];
